import { Link, useNavigate } from "react-router-dom";
import { Plus, Star, BadgeCheck } from "lucide-react";
import { StarRating } from "./StarRating";

function SizeChips({ p }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`size-chips-${p.id}`}>
      {p.variants.map((v) => (
        <button
          key={v.id}
          type="button"
          data-testid={`size-chip-${v.id}`}
          onClick={(e) => { e.preventDefault(); navigate(`/product/${v.id}`); }}
          className={`inline-flex items-center min-h-[32px] text-[11px] font-semibold px-2.5 py-1 rounded-none border transition-colors duration-200 ${
            v.in_stock
              ? "border-[#DDD7CF] text-[#6E675F] hover:border-[#8C7B6B] hover:text-[#1A1A1A]"
              : "border-dashed border-[#DDD7CF] text-[#6E675F]/50 line-through"
          }`}
          aria-label={`${p.name} ${v.dosage}`}
        >
          {v.dosage}
        </button>
      ))}
    </div>
  );
}

// Family card: `p` comes from groupByName() — one card per product name with its vial-size variants.
export function ProductCard({ p, i = 0, onAdd }) {
  const multi = p.variants.length > 1;
  return (
    <Link
      to={`/product/${p.id}`}
      data-testid={`product-card-${p.id}`}
      className="group animate-fade-in"
      style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}
    >
      <div className="relative bg-white rounded-lg overflow-hidden aspect-[4/3] mb-3 sm:mb-5 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_20px_50px_-20px_rgba(140,123,107,0.35)]">
        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
        {p.featured && (
          <span className="absolute top-2 left-2 sm:top-3 sm:left-3 inline-flex items-center gap-1 bg-[#8C7B6B] text-white text-[10px] tracking-[0.12em] uppercase font-semibold px-2.5 py-1 rounded-none" data-testid={`best-seller-badge-${p.id}`}>
            <Star className="w-3 h-3 fill-white" /> Best Seller
          </span>
        )}
        <span className="absolute top-2 right-2 sm:top-3 sm:right-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur text-[#2D6A4F] text-[10px] tracking-[0.1em] uppercase font-semibold px-2 py-1 sm:px-2.5 rounded-none border border-[#DDD7CF]" data-testid={`lab-certified-badge-${p.id}`}>
          <BadgeCheck className="w-3 h-3" /> <span className="hidden sm:inline">Lab Certified</span>
        </span>
        {multi && (
          <span className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 bg-[#1A1A1A]/80 backdrop-blur text-[#F7F5F2] text-[10px] tracking-[0.1em] uppercase font-semibold px-2.5 py-1 rounded-none" data-testid={`size-count-badge-${p.id}`}>
            {p.variants.length} sizes
          </span>
        )}
        {!p.in_stock && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <span className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F]">Out of Stock</span>
          </div>
        )}
      </div>
      <div className="flex items-start justify-between gap-2 sm:gap-4">
        <div className="min-w-0">
          <h3 className="font-product text-xs sm:text-base uppercase tracking-wider text-[#1A1A1A]">
            {p.name} {p.dosage && <span className="normal-case text-[#6E675F] text-[11px] sm:text-xs">{p.dosage}</span>}
          </h3>
          {p.rating_count > 0 && (
            <div className="mt-1"><StarRating value={p.rating_avg} size="w-3 h-3" showValue count={p.rating_count} /></div>
          )}
          <p className="text-sm sm:text-base text-[#1A1A1A] font-medium mt-1" data-testid={`product-price-${p.id}`}>
            {multi && p.maxPrice !== p.price && <span className="text-[11px] sm:text-xs text-[#6E675F] font-normal mr-1">From</span>}
            ${p.price.toFixed(2)} <span className="text-[11px] sm:text-xs text-[#6E675F]">CAD</span>
          </p>
          {multi && <SizeChips p={p} />}
        </div>
        {p.in_stock && !multi && onAdd && (
          <button
            data-testid={`add-to-cart-button-${p.id}`}
            onClick={(e) => onAdd(e, p.variants[0])}
            className="shrink-0 w-10 h-10 rounded-full border border-[#DDD7CF] flex items-center justify-center hover:bg-[#8C7B6B] hover:border-[#8C7B6B] transition-colors duration-300"
            aria-label={`Add ${p.name} to cart`}
          >
            <Plus className="w-4 h-4 text-[#1A1A1A]" />
          </button>
        )}
      </div>
    </Link>
  );
}

export const ProductGrid = ({ families, onAdd, cols = "grid-cols-2 lg:grid-cols-3", testid }) => (
  <div className={`grid ${cols} gap-x-4 gap-y-10 sm:gap-x-8 sm:gap-y-14`} data-testid={testid}>
    {families.map((p, i) => <ProductCard key={p.id} p={p} i={i} onAdd={onAdd} />)}
  </div>
);
